import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import logo from "../../assets/png/CodeNekt_for_Fleet_Logo.png";
import axios from "../../api/axios";
import { billingKeys, Subscription, readCheckoutReference, clearCheckoutReference } from "../../api/billing";
import { getCurrentFleetId } from "../../utils/getCurrentFleet";
import { AuthThemeScope } from "../../theme/AuthThemeScope";

/** Poll cadence and ceiling. 2s x 15 ~ 30s before we stop waiting. */
const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 15;

type Phase = "waiting" | "ready" | "timeout" | "error";

const isUsable = (sub: Subscription | null): boolean =>
  !!sub && (sub.status === "active" || sub.status === "trialing");

/**
 * Landing page after checkout succeeds.
 *
 * The provider redirects here the instant payment is authorised. The local
 * subscription row, by contrast, is written by a webhook delivered
 * out-of-band. Those two race and the redirect nearly always wins.
 *
 * The original version only polled, which meant the happy path depended on a
 * webhook arriving within 30 seconds -- and if webhooks were misconfigured or
 * a handler threw, the row never appeared at all and a paying customer was
 * simply stuck. So we now do the provisioning ourselves first:
 *
 *   1. POST /billing/reconcile with the checkout reference. The API reads the
 *      checkout straight from the provider and writes the subscription.
 *      Usually resolves on the first try, before the webhook has even been
 *      dispatched.
 *   2. Fall back to polling GET /billing/subscription. This covers a checkout
 *      still finalising (async payment methods, 3DS settling) and the case
 *      where the reference is missing entirely.
 *
 * Reconcile is idempotent, so step 1 racing the webhook is harmless: whichever
 * lands first writes the row, the other updates it in place.
 *
 * WHERE THE REFERENCE COMES FROM. Stripe substitutes {CHECKOUT_SESSION_ID}
 * into success_url, so it arrives in the query string. Revolut returns the
 * redirect URL verbatim with no placeholder, so the id is stashed in
 * sessionStorage before the redirect instead. The query string is preferred
 * when both exist -- it is the fresher of the two, and a stale stash from an
 * abandoned attempt must never win over the checkout that just completed.
 *
 * The poll stays bounded. Timing out shows a reassuring message and a manual
 * retry rather than spinning forever or implying the payment failed -- the
 * money has already been authorised and telling the user otherwise would be
 * both wrong and alarming.
 */
export const RegisterComplete: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const companyId = getCurrentFleetId();

  // Query string first (Stripe), stash second (Revolut). A query value that
  // came back with the placeholder unsubstituted is treated as absent.
  const querySessionId = searchParams.get("session_id");
  const checkoutReference =
    querySessionId && !querySessionId.includes("{")
      ? querySessionId
      : readCheckoutReference();

  const [phase, setPhase] = useState<Phase>("waiting");
  const [attempt, setAttempt] = useState(0);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;

    if (!companyId) {
      setPhase("error");
      return;
    }

    let attempts = 0;

    const succeed = () => {
      // Drop the cached null so the app doesn't immediately re-render as
      // "no subscription" once we navigate away.
      queryClient.invalidateQueries({
        queryKey: billingKeys.subscription(companyId),
      });
      // The reference has done its job. Leaving it would have a later visit to
      // this page reconciling against a subscription that is already live.
      clearCheckoutReference();
      if (!cancelled.current) setPhase("ready");
    };

    /**
     * Ask the API to provision from the completed checkout. Returns true when
     * the subscription is live.
     *
     * The API picks the provider from the shape of the reference, so both a
     * Stripe `cs_...` session and a Revolut subscription UUID are accepted
     * here without the client needing to know which provider is active.
     */
    const reconcile = async (): Promise<boolean> => {
      if (!checkoutReference) return false;

      try {
        const res = await axios.post(`/companies/${companyId}/billing/reconcile`, {
          session_id: checkoutReference,
        });
        return isUsable(res?.data?.data ?? null);
      } catch {
        // A 422 here means the reference isn't usable (not ours, not complete).
        // Neither is worth surfacing: the poll below is the safety net, and
        // the webhook may still land.
        return false;
      }
    };

    const poll = async (): Promise<void> => {
      if (cancelled.current) return;
      attempts += 1;
      setAttempt(attempts);

      try {
        const res = await axios.get(`/companies/${companyId}/billing/subscription`);
        if (isUsable(res?.data?.data ?? null)) {
          succeed();
          return;
        }
      } catch {
        // Swallow and retry: a transient 5xx mid-provisioning is expected and
        // is not a reason to tell the user their payment did not work.
      }

      // Retry reconciliation periodically too, not just on the first pass. A
      // session that was still finalising a moment ago may have completed, and
      // waiting on the webhook alone is exactly the dependency we removed.
      if (attempts % 3 === 0 && (await reconcile())) {
        succeed();
        return;
      }

      if (attempts >= MAX_ATTEMPTS) {
        if (!cancelled.current) setPhase("timeout");
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    };

    void (async () => {
      if (await reconcile()) {
        succeed();
        return;
      }
      void poll();
    })();

    return () => {
      cancelled.current = true;
    };
  }, [companyId, checkoutReference, queryClient]);

  // Once ready, give the success state a beat to register visually, then go.
  useEffect(() => {
    if (phase !== "ready") return;
    const id = setTimeout(() => navigate("/dashboard", { replace: true }), 1200);
    return () => clearTimeout(id);
  }, [phase, navigate]);

  return (
    // Same forced-dark scope as the rest of signup: this page sits between the
    // payment page and the dashboard, so switching palette here would flash a
    // white screen mid-flow for a user on the dark theme.
    <AuthThemeScope>
      <Box
        sx={{
          minHeight: "var(--app-vh)",
          display: "flex",
          alignItems: "center",
        }}
      >
      <Container maxWidth="sm">
        <Paper variant="outlined" sx={{ p: { xs: 3, md: 5 } }}>
          <Stack spacing={3} alignItems="center" textAlign="center">
            <img src={logo} alt="CodeNekt for Fleet" style={{ height: 48 }} />

            {phase === "waiting" && (
              <>
                <CircularProgress />
                <Typography variant="h6" fontWeight={700}>
                  {t("register.activating", "Activating your subscriptionâ€¦")}
                </Typography>
                <Typography color="text.secondary" fontSize={14}>
                  {t(
                    "register.activatingHint",
                    "Your payment went through. We're setting up your fleet â€” this usually takes a few seconds."
                  )}
                </Typography>
                {attempt > 5 && (
                  <Typography color="text.secondary" fontSize={12.5}>
                    {t("register.activatingSlow", "Still working â€” thanks for your patience.")}
                  </Typography>
                )}
              </>
            )}

            {phase === "ready" && (
              <>
                <CheckCircleRoundedIcon color="success" sx={{ fontSize: 56 }} />
                <Typography variant="h6" fontWeight={700}>
                  {t("register.ready", "You're all set")}
                </Typography>
                <Typography color="text.secondary" fontSize={14}>
                  {t("register.readyHint", "Taking you to your dashboardâ€¦")}
                </Typography>
              </>
            )}

            {phase === "timeout" && (
              <>
                <Alert severity="info" sx={{ textAlign: "left" }}>
                  {t(
                    "register.activationDelayed",
                    "Your payment was accepted, but activation is taking longer than usual. It will complete automatically â€” you can refresh in a minute or head to Billing."
                  )}
                </Alert>
                <Stack direction="row" spacing={1.5}>
                  <Button variant="contained" onClick={() => window.location.reload()}>
                    {t("common.retry", "Retry")}
                  </Button>
                  <Button variant="outlined" onClick={() => navigate("/billing")}>
                    {t("sidebar.billing", "Billing")}
                  </Button>
                </Stack>
              </>
            )}

            {phase === "error" && (
              <>
                <Alert severity="warning" sx={{ textAlign: "left" }}>
                  {t(
                    "register.errorNoSession",
                    "We couldn't identify your account. Please sign in to continue."
                  )}
                </Alert>
                <Button variant="contained" onClick={() => navigate("/")}>
                  {t("login.login", "Login")}
                </Button>
              </>
            )}
          </Stack>
        </Paper>
      </Container>
      </Box>
    </AuthThemeScope>
  );
};

export default RegisterComplete;
